import { create } from 'zustand'

// Типы для системы авторизации
export type AuthMode = 'telegram' | 'browser' | 'unknown'
export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated' | 'needs_registration' | 'pending_approval'

export interface User {
  id: number
  contact_id?: number  // Для браузерной авторизации
  telegram_id?: number
  user_id?: number
  first_name: string
  last_name?: string
  name?: string
  username?: string
  phone?: string
  email?: string
  is_premium?: boolean
  language_code?: string
  photo_url?: string
  client_id?: number | string
}

export interface AuthState {
  // Основное состояние
  status: AuthStatus
  token: string | null
  user: User | null
  mode: AuthMode
  
  // Telegram-специфичные данные
  telegramId: string | null
  initData: string | null
  webToken: string | null
  
  // WebSocket ID (contact_id для браузера, telegram_id для Telegram)
  webSocketUserId: string | null
  
  // Флаги состояния
  needsRegistration: boolean
  pendingApproval: boolean
  error: string | null
  isCheckingAccess: boolean
  
  // Действия
  setAuth: (token: string, user: User, mode: AuthMode) => void
  setTelegramData: (telegramId: string, initData?: string) => void
  setWebToken: (webToken: string) => void
  setStatus: (status: AuthStatus) => void
  setError: (error: string | null) => void
  setNeedsRegistration: (needs: boolean) => void
  setPendingApproval: (pending: boolean) => void
  setCheckingAccess: (isChecking: boolean) => void
  clear: () => void
  updateUser: (partial: Partial<User>) => void
  
  // Геттеры
  isAuthenticated: () => boolean
  isTelegramMode: () => boolean
  isBrowserMode: () => boolean
  
  // Функции для работы с токенами
  isTokenExpired: () => boolean
  isTokenExpiringSoon: (minutesThreshold?: number) => boolean
  refreshTokenIfNeeded: () => Promise<boolean>
}

// Функции для работы с localStorage
const getFromStorage = (key: string): string | null => {
  if (typeof localStorage === 'undefined') return null
  return localStorage.getItem(key)
}

const setToStorage = (key: string, value: string | null) => {
  if (typeof localStorage === 'undefined') return
  if (value) {
    localStorage.setItem(key, value)
  } else {
    localStorage.removeItem(key)
  }
}

export const useAuthStore = create<AuthState>((set, get) => ({
  // Инициализация из localStorage
  status: 'loading',
  token: (() => {
    const token = getFromStorage('alfastil_token')
    // 🔥 ИСПРАВЛЕНИЕ: Проверяем токен при инициализации
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]))
        const now = Math.floor(Date.now() / 1000)
        if (payload.exp && payload.exp < now) {
          console.log('🔥 ОТЛАДКА: Истекший токен найден при инициализации, очищаем')
          localStorage.removeItem('alfastil_token')
          localStorage.removeItem('alfastil_user')
          return null
        }
      } catch (e) {
        console.log('🔥 ОТЛАДКА: Поврежденный токен найден при инициализации, очищаем')
        localStorage.removeItem('alfastil_token')
        localStorage.removeItem('alfastil_user')
        return null
      }
    }
    return token
  })(),
  user: (() => {
    const userData = getFromStorage('alfastil_user')
    return userData ? JSON.parse(userData) : null
  })(),
  mode: 'unknown',
  
  telegramId: getFromStorage('telegram_id'),
  initData: null,
  webToken: (() => {
    const webToken = getFromStorage('alfastil_web_token')
    // 🔥 ИСПРАВЛЕНИЕ: Проверяем webToken при инициализации
    if (webToken) {
      try {
        const payload = JSON.parse(atob(webToken.split('.')[1]))
        const now = Math.floor(Date.now() / 1000)
        if (payload.exp && payload.exp < now) {
          console.log('🔥 ОТЛАДКА: Истекший webToken найден при инициализации, очищаем')
          localStorage.removeItem('alfastil_web_token')
          return null
        }
      } catch (e) {
        console.log('🔥 ОТЛАДКА: Поврежденный webToken найден при инициализации, очищаем')
        localStorage.removeItem('alfastil_web_token')
        return null
      }
    }
    return webToken
  })(),
  
  // WebSocket ID: определяется по режиму при авторизации
  webSocketUserId: null, // Будет установлен при setAuth
  
  needsRegistration: false,
  pendingApproval: false,
  error: null,
  isCheckingAccess: false, // Новое состояние для проверки доступа
  
  // Действия
  setAuth: (token, user, mode) => {
    setToStorage('alfastil_token', token)
    setToStorage('alfastil_user', JSON.stringify(user))
    
    // ПРАВИЛЬНАЯ АРХИТЕКТУРА: сохраняем ID в зависимости от режима
    let webSocketUserId = null
    

    
    if (mode === 'telegram') {
      // Telegram режим: используем telegram_id для WebSocket
      webSocketUserId = String(user.telegram_id || user.user_id || user.id || '')
      if (webSocketUserId) {
        setToStorage('telegram_id', webSocketUserId)

      }
    } else if (mode === 'browser') {
      // Браузер режим: используем contact_id для WebSocket, НЕ telegram_id!
      webSocketUserId = String(user.contact_id || user.id || '') // contact_id
      if (webSocketUserId) {
        setToStorage('websocket_user_id', webSocketUserId) // Отдельное хранилище

      }
      // Очищаем telegram_id для браузера
      localStorage.removeItem('telegram_id')
    }
    

    
    set({ 
      status: 'authenticated',
      token, 
      user, 
      mode,
      telegramId: mode === 'telegram' ? webSocketUserId : null, // Только для Telegram!
      webSocketUserId, // Универсальный ID для WebSocket
      error: null,
      needsRegistration: false,
      pendingApproval: false,
      isCheckingAccess: false // ВАЖНО: Сбрасываем флаг проверки доступа при успешной авторизации
    })
  },
  
  setTelegramData: (telegramId, initData) => {
    setToStorage('telegram_id', telegramId)
    set({ telegramId, initData, mode: 'telegram', webSocketUserId: telegramId })
  },
  
  setWebToken: (webToken) => {
    setToStorage('alfastil_web_token', webToken)
    set({ webToken, mode: 'browser' })
  },
  
  setStatus: (status) => set({ status }),
  setError: (error) => set({ error }),
  setNeedsRegistration: (needsRegistration) => set({ needsRegistration }),
  setPendingApproval: (pendingApproval) => set({ pendingApproval }),
  setCheckingAccess: (isCheckingAccess) => set({ isCheckingAccess }),
  
  clear: () => {
    // Очищаем localStorage (НЕ очищаем telegram_id - это способ входа)
    setToStorage('alfastil_token', null)
    setToStorage('alfastil_user', null)
    // setToStorage('telegram_id', null) // НЕ очищаем - нужно помнить что зашли через Telegram
    setToStorage('alfastil_web_token', null)
    setToStorage('websocket_user_id', null) // Очищаем WebSocket ID для браузера
    
    const currentState = get()
    
    // Определяем режим: если есть Telegram WebApp API, то telegram, иначе browser
    const isTelegramEnv = typeof window !== 'undefined' && 
      (!!window.location.pathname.startsWith('/miniapp') || 
       !!(window as any)?.Telegram?.WebApp)
    
    set({
      status: 'unauthenticated',
      token: null,
      user: null,
      mode: isTelegramEnv ? 'telegram' : 'browser',
      // В браузере НЕ должно быть telegram_id!
      telegramId: isTelegramEnv ? currentState.telegramId : null,
      webSocketUserId: null, // Очищаем полностью
      initData: null,
      webToken: null,
      needsRegistration: false,
      pendingApproval: false,
      error: null,
      isCheckingAccess: false
    })
  },
  
  updateUser: (partial) => {
    const state = get()
    const current = state.user
    if (!current) return
    const updated = { ...current, ...partial }
    setToStorage('alfastil_user', JSON.stringify(updated))
    // Обновим telegram_id, если появилось user_id/telegram_id/id
    const telegramId = String(updated.telegram_id || updated.user_id || updated.id || '')
    if (telegramId) setToStorage('telegram_id', telegramId)
    set({ user: updated })
  },
  
  // Геттеры
  isAuthenticated: () => {
    const state = get()
    return state.status === 'authenticated' && !!state.token && !!state.user
  },
  
  isTelegramMode: () => get().mode === 'telegram',
  isBrowserMode: () => get().mode === 'browser',
  
  // Функции для работы с токенами
  isTokenExpired: () => {
    const token = get().token
    if (!token) return true
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      const now = Math.floor(Date.now() / 1000)
      return payload.exp && payload.exp < now
    } catch {
      return true
    }
  },
  
  isTokenExpiringSoon: (minutesThreshold = 30) => {
    const token = get().token
    if (!token) return true
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      const now = Math.floor(Date.now() / 1000)
      const threshold = now + (minutesThreshold * 60)
      return payload.exp && payload.exp < threshold
    } catch {
      return true
    }
  },
  
  refreshTokenIfNeeded: async () => {
    const state = get()
    
    // Если токен не истекает в ближайшие 30 минут, не обновляем
    if (!state.isTokenExpiringSoon()) {
      return true
    }
    
    try {
      console.log('🔄 Токен скоро истечет, пытаемся обновить...')
      
      // Для браузерного режима - используем новый эндпоинт refresh
      if (state.mode === 'browser' && state.token) {
        console.log('🔄 Браузерный режим: используем эндпоинт /web_auth/refresh')
        
        try {
          // Импортируем http динамически чтобы избежать циклических зависимостей
          const { http } = await import('../api/http')
          
          const response = await http.post('/web_auth/refresh')
          
          if (response.data?.success && response.data?.token && response.data?.user) {
            console.log('✅ Токен успешно обновлен через /web_auth/refresh')
            
            // Обновляем состояние с новым токеном
            const { setAuth } = get()
            setAuth(response.data.token, response.data.user, 'browser')
            
            return true
          } else {
            console.warn('⚠️ Не удалось обновить токен через /web_auth/refresh:', response.data?.message)
          }
        } catch (refreshError) {
          console.warn('⚠️ Ошибка при обновлении токена через /web_auth/refresh:', refreshError)
        }
        
        // Fallback: пытаемся переавторизоваться
        console.log('🔄 Fallback: пытаемся переавторизоваться')
        const { authService } = await import('./authService')
        const result = await authService.initializeAuth()
        
        if (result) {
          console.log('✅ Токен успешно обновлен через браузерную авторизацию (fallback)')
          return true
        } else {
          console.warn('⚠️ Не удалось обновить токен через браузерную авторизацию (fallback)')
          return false
        }
      }
      
      // Для Telegram режима - повторная авторизация через initData
      if (state.mode === 'telegram' && state.initData) {
        console.log('🔄 Telegram режим: пытаемся переавторизоваться')
        
        const { authService } = await import('./authService')
        const result = await authService.initializeAuth()
        
        if (result) {
          console.log('✅ Токен успешно обновлен через Telegram авторизацию')
          return true
        } else {
          console.warn('⚠️ Не удалось обновить токен через Telegram авторизацию')
          return false
        }
      }
      
      console.warn('⚠️ Не удалось определить способ обновления токена')
      return false
    } catch (error) {
      console.error('❌ Ошибка при обновлении токена:', error)
      return false
    }
  }
}))

// Хук для удобного использования статуса аутентификации
export const useAuth = () => {
  const store = useAuthStore()
  return {
    ...store,
    isLoading: store.status === 'loading',
    isAuthenticated: store.isAuthenticated(),
    isTelegramMode: store.isTelegramMode(),
    isBrowserMode: store.isBrowserMode()
  }
}